// @ts-strict-ignore
import * as React from 'react';
import { CheckCircleOutlined, CloseCircleOutlined, ExclamationCircleOutlined, MehTwoTone } from '@ant-design/icons';
import Alert from 'antd/lib/alert/Alert';
import Button from 'antd/lib/button';
import { Skeleton } from 'antd/lib';
import Space from 'antd/lib/space';
import Text from 'antd/lib/typography/Text';
import { useSelector } from 'react-redux';
import userflow from 'userflow.js';
import { OrgInsightsResponse } from '@/actions/entities';
import MarkdownComponent from '@/components/HOCs/Markdown';
import MarkdownParagraphWithEllipsis from '@/components/HOCs/MarkdownParagraphWithEllipsis';
import { colorWithAlpha, runwayColors } from '@/components/styles/globalStyles';
import { displayOrgOverviewStatisticLabel, GetAppNameWithPlatform } from '@/components/orgOverview/orgOverviewHelpers';
import { RootState } from '@/reducers/reducers';
import { selectAllAppNamesAndPlatformsByAppId } from '@/selectors/selectors';
import { selectDestinationsData } from '@/selectors/summaryViewSelectors';
import { AppPlatform, StatisticType } from '@/types';
import { Components } from 'react-markdown';

interface OrgOverviewInsightsAlertProps {
    orgId: string;
    insightsAlert?: OrgInsightsResponse;
    isLoading: boolean;
    onClose: () => void;
}

export enum InsightsAlertType {
    Info = 'info',
    Warning = 'warning',
    Error = 'error',
    Success = 'success',
}

enum InsightsAlertTokenType {
    App = 'app',
    Destination = 'destination',
    Metric = 'metric',
}

export const OrgOverviewInsightsAlert: React.FC<OrgOverviewInsightsAlertProps> = ({
    orgId,
    insightsAlert,
    isLoading,
    onClose,
}) => {
    const METRIC_SCROLL_TOP_OFFSET_PX = 100;

    const findScrollableParent = React.useCallback((element: HTMLElement) => {
        let currentParent = element.parentElement;

        while (currentParent) {
            const { overflowY } = window.getComputedStyle(currentParent);
            const isScrollable = ['auto', 'scroll', 'overlay'].includes(overflowY);

            if (isScrollable && currentParent.scrollHeight > currentParent.clientHeight) {
                return currentParent;
            }

            currentParent = currentParent.parentElement;
        }

        return null;
    }, []);

    const scrollToMetric = React.useCallback(
        (metricId: string) => {
            const element = document.getElementById(metricId);
            if (!element) {
                return;
            }

            const scrollableParent = findScrollableParent(element);

            if (scrollableParent) {
                const top =
                    scrollableParent.scrollTop +
                    element.getBoundingClientRect().top -
                    scrollableParent.getBoundingClientRect().top -
                    METRIC_SCROLL_TOP_OFFSET_PX;

                scrollableParent.scrollTo({ top, behavior: 'smooth' });
                return;
            }

            const top = window.scrollY + element.getBoundingClientRect().top - METRIC_SCROLL_TOP_OFFSET_PX;
            window.scrollTo({ top, behavior: 'smooth' });
        },
        [findScrollableParent],
    );

    const markdownComponents = React.useMemo<Components>(
        () => ({
            a: ({ href, children, ...props }) => {
                if (href?.startsWith('#')) {
                    const metricId = href.slice(1);
                    return (
                        <a
                            {...props}
                            href={href}
                            onClick={(event) => {
                                event.preventDefault();
                                scrollToMetric(metricId);
                            }}
                            style={{
                                color: runwayColors.primary,
                                textDecorationLine: 'underline',
                                textDecorationStyle: 'dotted',
                                textUnderlineOffset: 3,
                                cursor: 'pointer',
                            }}
                        >
                            {children}
                        </a>
                    );
                }

                return (
                    <a {...props} href={href} target="_blank" rel="noreferrer">
                        {children}
                    </a>
                );
            },
        }),
        [scrollToMetric],
    );

    const appNamesAndPlatformsByAppId = useSelector(selectAllAppNamesAndPlatformsByAppId);
    const orgDestinationsData = useSelector((state: RootState) => selectDestinationsData(state, orgId));

    const insightsDestinationNamesById = React.useMemo(() => {
        return (orgDestinationsData?.appDestinations ?? []).reduce(
            (acc, { appName, destinations }) => {
                for (const destination of destinations) {
                    if (!destination.destinationId) {
                        continue;
                    }

                    const appInfo = destination.appId ? appNamesAndPlatformsByAppId[destination.appId] : null;
                    const platform = (destination.platform as AppPlatform) || appInfo?.platform;
                    const fallbackName =
                        appName && platform ?
                            GetAppNameWithPlatform(appName, platform)
                        :   appName || destination.destinationId;

                    acc[destination.destinationId] = destination.name || fallbackName;
                    acc[destination.stepConfigId] = destination.name || fallbackName;
                }

                return acc;
            },
            {} as Record<string, string>,
        );
    }, [appNamesAndPlatformsByAppId, orgDestinationsData?.appDestinations]);

    const insightsAppNamesById = React.useMemo(
        () =>
            Object.entries(appNamesAndPlatformsByAppId).reduce(
                (acc, [appId, appInfo]) => {
                    acc[appId] =
                        appInfo?.name && appInfo?.platform ?
                            GetAppNameWithPlatform(appInfo.name, appInfo.platform)
                        :   appInfo?.name || appId;
                    return acc;
                },
                {} as Record<string, string>,
            ),
        [appNamesAndPlatformsByAppId],
    );

    const formatInsightsText = React.useCallback(
        (text?: string) => {
            if (!text) {
                return text;
            }

            return text.replace(
                /\$\{(destination|app|metric):([^}]+)\}/g,
                (_match, tokenType: InsightsAlertTokenType, rawId: string) => {
                    const id = rawId.trim();
                    let resolvedText = id;

                    switch (tokenType) {
                        case InsightsAlertTokenType.Destination:
                            resolvedText = insightsDestinationNamesById[id] || id;
                            break;
                        case InsightsAlertTokenType.App:
                            resolvedText = insightsAppNamesById[id] || id;
                            break;
                        case InsightsAlertTokenType.Metric:
                            resolvedText = displayOrgOverviewStatisticLabel(id as StatisticType) || id;
                            return `[${resolvedText}](#${id})`;
                        default:
                            resolvedText = id;
                    }

                    return `**${resolvedText}**`;
                },
            );
        },
        [insightsAppNamesById, insightsDestinationNamesById],
    );

    const getInsightsAlertColor = (): string => {
        switch (insightsAlert?.type) {
            case InsightsAlertType.Success:
                return runwayColors.statusGreen;
            case InsightsAlertType.Error:
                return runwayColors.statusRed;
            case InsightsAlertType.Warning:
                return runwayColors.statusYellow;
            case InsightsAlertType.Info:
                return colorWithAlpha(runwayColors.primary, 0.25);
            default:
                return null;
        }
    };

    const getInsightsAlertIcon = (): React.ReactNode => {
        const iconStyle = { fontSize: 18, marginTop: 3, color: getInsightsAlertColor() };
        switch (insightsAlert?.type) {
            case InsightsAlertType.Success:
                return <CheckCircleOutlined style={iconStyle} />;
            case InsightsAlertType.Error:
                return <CloseCircleOutlined style={iconStyle} />;
            case InsightsAlertType.Warning:
                return <ExclamationCircleOutlined style={iconStyle} />;
            case InsightsAlertType.Info:
                return <MehTwoTone style={iconStyle} />;
            default:
                return null;
        }
    };

    const showUserflowInsightsFeedback = () => {
        userflow.start('9bcc579e-33ba-4018-821a-727301341e41');
    };

    return (
        <Alert
            type={insightsAlert?.type}
            closable
            onClose={onClose}
            style={{
                width: 800,
                marginTop: 20,
                padding: 19,
                border: `1px solid ${getInsightsAlertColor()}`,
                borderRadius: 4,
                alignItems: 'flex-start',
            }}
            message={
                isLoading || !insightsAlert ?
                    <Skeleton
                        active
                        round={false}
                        style={{
                            padding: 12,
                        }}
                        paragraph={{
                            rows: 3,
                        }}
                    />
                :   <Space style={{ alignItems: 'flex-start' }} size={16}>
                        {getInsightsAlertIcon()}
                        <div>
                            <MarkdownComponent
                                source={formatInsightsText(insightsAlert.title)}
                                components={markdownComponents}
                                style={{ fontSize: 16, marginBottom: 8 }}
                            />
                            <MarkdownParagraphWithEllipsis
                                content={formatInsightsText(insightsAlert.description)}
                                maxHeight="250px"
                                style={{ fontSize: 14, marginBottom: 8 }}
                                backgroundColor="transparent"
                                markdownProps={{ components: markdownComponents }}
                            />
                            <Text type="secondary" style={{ marginTop: 8 }}>
                                AI insights are in beta.{' '}
                                <Button
                                    type="link"
                                    size="small"
                                    onClick={showUserflowInsightsFeedback}
                                    style={{ padding: 0 }}
                                >
                                    Send feedback?
                                </Button>
                            </Text>
                        </div>
                    </Space>
            }
        />
    );
};
